const DB_NAME = "whitewater-capture-recovery";
const DB_VERSION = 1;
const SESSION_STORE = "sessions";
const CHUNK_STORE = "chunks";

const requestResult = (request) => new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
});

const transactionDone = (transaction) => new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error || new Error("IndexedDB transaction aborted"));
});

const openDatabase = () => new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
        reject(new Error("This browser does not support recording recovery storage."));
        return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(SESSION_STORE)) {
            database.createObjectStore(SESSION_STORE, { keyPath: "capture_session_id" });
        }
        if (!database.objectStoreNames.contains(CHUNK_STORE)) {
            const chunks = database.createObjectStore(CHUNK_STORE, { keyPath: ["capture_session_id", "index"] });
            chunks.createIndex("capture_session_id", "capture_session_id", { unique: false });
        }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Could not open recording recovery storage."));
});

export async function startPendingRecording(session) {
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, "readwrite");
    transaction.objectStore(SESSION_STORE).put({
        ...session,
        status: "recording",
        chunk_count: 0,
        total_bytes: 0,
        created_at: session.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
    });
    await transactionDone(transaction);
    database.close();
}

export async function appendPendingRecordingChunk(captureSessionId, index, blob) {
    if (!captureSessionId || !blob?.size) return;
    const database = await openDatabase();
    const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], "readwrite");
    const sessions = transaction.objectStore(SESSION_STORE);
    const current = await requestResult(sessions.get(captureSessionId));
    if (!current) {
        transaction.abort();
        database.close();
        throw new Error("Pending recording metadata was not found.");
    }
    transaction.objectStore(CHUNK_STORE).put({
        capture_session_id: captureSessionId,
        index,
        blob,
        bytes: blob.size,
        created_at: new Date().toISOString(),
    });
    sessions.put({
        ...current,
        chunk_count: Math.max(Number(current.chunk_count || 0), index + 1),
        total_bytes: Number(current.total_bytes || 0) + blob.size,
        updated_at: new Date().toISOString(),
    });
    await transactionDone(transaction);
    database.close();
}

export async function patchPendingRecording(captureSessionId, patch) {
    if (!captureSessionId) return;
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, "readwrite");
    const store = transaction.objectStore(SESSION_STORE);
    const current = await requestResult(store.get(captureSessionId));
    if (current) store.put({ ...current, ...patch, updated_at: new Date().toISOString() });
    await transactionDone(transaction);
    database.close();
}

export async function listRecoverableRecordings() {
    const database = await openDatabase();
    const transaction = database.transaction(SESSION_STORE, "readonly");
    const sessions = await requestResult(transaction.objectStore(SESSION_STORE).getAll());
    await transactionDone(transaction);
    database.close();
    return sessions
        .filter((session) => session?.capture_session_id && Number(session.chunk_count || 0) > 0 && session.status !== "saved")
        .sort((a, b) => String(a.updated_at || "").localeCompare(String(b.updated_at || "")));
}

export async function loadRecoverableRecording(captureSessionId) {
    const database = await openDatabase();
    const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], "readonly");
    const session = await requestResult(transaction.objectStore(SESSION_STORE).get(captureSessionId));
    const chunks = await requestResult(
        transaction.objectStore(CHUNK_STORE).index("capture_session_id").getAll(captureSessionId),
    );
    await transactionDone(transaction);
    database.close();
    return {
        session,
        chunks: chunks.sort((a, b) => Number(a.index) - Number(b.index)),
    };
}

export async function deleteRecoverableRecording(captureSessionId) {
    const database = await openDatabase();
    const transaction = database.transaction([SESSION_STORE, CHUNK_STORE], "readwrite");
    transaction.objectStore(SESSION_STORE).delete(captureSessionId);
    const index = transaction.objectStore(CHUNK_STORE).index("capture_session_id");
    const cursorRequest = index.openCursor(IDBKeyRange.only(captureSessionId));
    cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
    };
    await transactionDone(transaction);
    database.close();
}
